import * as THREE from "three";

export class GlowDisplay {
  private readonly scene = new THREE.Scene();
  private texture: THREE.FramebufferTexture | null = null;
  private readonly size = new THREE.Vector2();
  private readonly material = new THREE.ShaderMaterial({
    depthTest: false, depthWrite: false, toneMapped: false,
    uniforms: {
      source: { value: null },
      sourceSize: { value: new THREE.Vector2(1, 1) },
      viewRect: { value: new THREE.Vector4(0, 0, 1, 1) },
    },
    vertexShader: /* glsl */`
      varying vec2 viewUv;
      void main() {
        viewUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      uniform sampler2D source;
      uniform vec2 sourceSize;
      uniform vec4 viewRect;
      varying vec2 viewUv;

      vec3 sampleView(vec2 uv) {
        // Clamp within this eye's tile, including the bilinear filter footprint.
        // Glow must never sample a neighboring Looking Glass view.
        vec2 inset = 0.5 / viewRect.zw;
        vec2 quiltUv = (viewRect.xy + clamp(uv, inset, 1.0 - inset) * viewRect.zw) / sourceSize;
        return texture2D(source, quiltUv).rgb;
      }

      vec3 highlights(vec2 uv) {
        vec3 color = sampleView(uv);
        float brightness = max(color.r, max(color.g, color.b));
        return color * smoothstep(0.5, 0.85, brightness);
      }

      void main() {
        vec3 color = sampleView(viewUv);
        vec3 bloom = vec3(0.0);
        vec2 radius = vec2(viewRect.w / viewRect.z, 1.0) * 0.0045;
        for (int i = 0; i < 8; i++) {
          float angle = float(i) * 0.7853982;
          vec2 ray = vec2(cos(angle), sin(angle)) * radius;
          bloom += highlights(viewUv + ray) * 0.7 + highlights(viewUv + ray * 2.4) * 0.3;
        }
        color += bloom * (0.6 / 8.0);
        // The copied framebuffer already contains tone mapping and output
        // encoding. Keep those exact colors rather than converting them twice.
        gl_FragColor = vec4(max(color, 0.0), 1.0);
      }
    `,
  });

  constructor() {
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    quad.layers.enableAll();
    // Three renders this quad once for each XR subcamera. The shader's UVs
    // are local to that view so the glow stays consistent across the quilt.
    quad.onBeforeRender = (_renderer, _scene, camera) => {
      const viewport = (camera as THREE.PerspectiveCamera).viewport;
      this.material.uniforms.viewRect!.value.set(...(viewport
        ? [viewport.x, viewport.y, viewport.z, viewport.w] as const
        : [0, 0, this.size.x, this.size.y] as const));
      this.material.uniformsNeedUpdate = true;
    };
    this.scene.add(quad);
  }

  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, enabled = true): void {
    renderer.render(scene, camera);
    if (!enabled) { this.releaseTexture(); return; }
    const output = renderer.getRenderTarget();
    if (output) this.size.set(output.width, output.height);
    else renderer.getDrawingBufferSize(this.size);
    if (!this.texture || this.texture.image.width !== this.size.x || this.texture.image.height !== this.size.y) {
      this.releaseTexture();
      this.texture = new THREE.FramebufferTexture(this.size.x, this.size.y);
      this.texture.minFilter = this.texture.magFilter = THREE.LinearFilter;
      this.material.uniforms.source!.value = this.texture;
    }
    // Copy the completed quilt once, without rebuilding the XR framebuffer or
    // allocating a second depth buffer. The SDK still interlaces the result.
    renderer.copyFramebufferToTexture(this.texture);
    this.material.uniforms.sourceSize!.value.copy(this.size);
    renderer.render(this.scene, camera);
  }

  private releaseTexture(): void {
    this.texture?.dispose();
    this.texture = null;
    this.material.uniforms.source!.value = null;
  }
}
